import React, { useState } from 'react';
import { Button, Row, Col } from 'antd';

import {
  BOUserTypeEnum,
  FullBOUserResponseModel,
  ResourceDownloadResponse,
} from 'api/backofficeApiClient';
import { getEinTypeLabel, isEinOption } from 'utils/entityUtils';
import { getMaskedNumberValue } from 'utils/numberUtils';
import { einMask, ssnMask } from 'utils/validationUtils';
import BasePageCard from 'components/wrappers/BasePageCard';
import BaseAlert from 'components/wrappers/BaseAlert';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import { LoadRecruiterDetailsOptions } from 'features/recruiter/RecruiterDetailsPage/types';
import EditEntityInfoModal from 'features/account/PrimaryInfo/EntityInfo/EditEntityInfoModal';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getRecruiterEntityInfoFile } from 'redux/reducers/recruiterSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';

type Props = {
  recruiterDetails: FullBOUserResponseModel;
  entityInfoFile?: ResourceDownloadResponse;
  loadRecruiterDetails: (
    options?: LoadRecruiterDetailsOptions,
  ) => Promise<void>;
};

export default function EntityInfoSection(props: Props) {
  const dispatch = useAppDispatch();
  const { recruiterDetails, entityInfoFile, loadRecruiterDetails } = props;
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { boUserType, businessInfo, companyId, userId } = recruiterDetails;

  const handleDownloadW8W9 = async () => {
    const result = await dispatch(getRecruiterEntityInfoFile(userId));

    if (getRecruiterEntityInfoFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleOpenEditModal = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const onEditEntityInfo = async () => {
    await loadRecruiterDetails({ shouldLoadEntityFile: true });
  };

  const taxIdMask = isEinOption(businessInfo?.entityInformation?.numberType)
    ? einMask
    : ssnMask;

  return (
    <BasePageCard
      title="Entity information"
      className="stretch-card"
      icons={<span className="icon icon-empire-state"></span>}
      subTitle={
        <Button
          onClick={handleOpenEditModal}
          className="with-icon mobile-icon-only"
          size="large"
          icon={<span className="icon icon-edit" />}
        >
          Edit
        </Button>
      }
    >
      <div className="content-wrapper">
        {isEditModalOpen && (
          <EditEntityInfoModal
            companyId={companyId}
            userType={boUserType}
            entityInfo={businessInfo?.entityInformation}
            onEdit={onEditEntityInfo}
            onClose={handleCloseEditModal}
            entityInfoFile={entityInfoFile}
          />
        )}

        {!businessInfo?.entityInformation && (
          <BaseSectionMessage
            title=""
            type="info"
            message={
              <>
                {boUserType === BOUserTypeEnum.Company && (
                  <span>
                    Waiting for member to respond. Entity information will be
                    displayed after a member enters the data.
                  </span>
                )}

                {boUserType === BOUserTypeEnum.SelfEmployed && (
                  <span>
                    Waiting for member to respond. Entity information will be
                    displayed after member enters the data.
                  </span>
                )}

                {boUserType === BOUserTypeEnum.International && (
                  <span>
                    Waiting for member to respond. Entity information will be
                    displayed after member enters data.
                  </span>
                )}
              </>
            }
          />
        )}

        {!!businessInfo?.entityInformation && (
          <Row gutter={[4, 4]} className="titled-sheet">
            {boUserType === BOUserTypeEnum.Company && (
              <>
                {!!businessInfo.entityInformation.name && (
                  <Col xs={24} md={12}>
                    <div className="title">Legal company name:</div>
                    <div>{businessInfo.entityInformation?.name}</div>
                  </Col>
                )}

                {!!businessInfo.entityInformation?.number && (
                  <Col xs={24} md={12}>
                    <div className="title">EIN:</div>
                    <div>
                      {getMaskedNumberValue(
                        businessInfo.entityInformation?.number,
                        einMask,
                      )}
                    </div>
                  </Col>
                )}
              </>
            )}

            {boUserType === BOUserTypeEnum.SelfEmployed && (
              <>
                {!!businessInfo.entityInformation?.name && (
                  <Col xs={24} md={12}>
                    <div className="title">Legal name:</div>
                    <div>{businessInfo.entityInformation?.name}</div>
                  </Col>
                )}

                {!!businessInfo.entityInformation?.number &&
                  !!businessInfo.entityInformation?.numberType && (
                    <Col xs={24} md={12}>
                      <div className="title">
                        {getEinTypeLabel(
                          businessInfo.entityInformation.numberType,
                        )}
                        :
                      </div>
                      <div>
                        {getMaskedNumberValue(
                          businessInfo.entityInformation.number,
                          taxIdMask,
                        )}
                      </div>
                    </Col>
                  )}
              </>
            )}

            {boUserType === BOUserTypeEnum.International && (
              <>
                {!!businessInfo.entityInformation?.name && (
                  <Col xs={24} md={12}>
                    <div className="title">Legal company name:</div>
                    <div>{businessInfo.entityInformation?.name}</div>
                  </Col>
                )}

                {!!businessInfo.entityInformation?.number && (
                  <Col xs={24} md={12}>
                    <div className="title">TIN:</div>
                    <div>{businessInfo.entityInformation?.number}</div>
                  </Col>
                )}

                {!!businessInfo.entityInformation?.countryName && (
                  <Col xs={24} md={12}>
                    <div className="title">Country of incorporation:</div>
                    <div>{businessInfo.entityInformation?.countryName}</div>
                  </Col>
                )}
              </>
            )}

            {!!entityInfoFile && (
              <Col xs={24} md={12}>
                <div className="title">
                  {boUserType === BOUserTypeEnum.International
                    ? 'Download W8:'
                    : 'Download W9:'}
                </div>

                <span className="link-imitation" onClick={handleDownloadW8W9}>
                  {getFileNameWithoutExt(entityInfoFile.fileName)}
                </span>
              </Col>
            )}
          </Row>
        )}
      </div>
    </BasePageCard>
  );
}
